<template>
  <b-card class="image-uploader border">
    <b-card-title class="d-flex align-items-center">
      <div class="ml-auto">
        <b-button variant="primary" size="sm" @click="openFileSelector">
          <i class="fa fa-cloud-upload-alt mr-2"></i>
          Chọn tập tin
        </b-button>
      </div>
    </b-card-title>

    <b-card-body>
      <file-upload
        ref="fileUpload"
        v-model="files"
        :multiple="true"
        :drop="true"
        :drop-directory="false"
        :maximum="10"
        :size="10 * 1024 * 1024"
        :thread="3"
        @input-filter="inputFilter"
        class="d-none"
      >
      </file-upload>

      <b-alert v-if="dropActive" show variant="info" class="drop-active">
        Thả tập tin vào đây để tải lên
      </b-alert>

      <div v-if="files.length === 0" class="text-center mt-4 mb-4">
        <i class="fa fa-images fa-3x text-muted"></i>
        <p class="text-muted mt-2">
          Kéo và thả hình ảnh vào đây hoặc nhấp vào nút "Chọn tập tin"
        </p>
      </div>

      <b-list-group v-else>
        <b-list-group-item
          v-for="(file, index) in files"
          :key="index"
          class="d-flex align-items-center py-3"
        >
          <div class="mr-3 file-thumbnail">
            <b-img
              v-if="file.thumb"
              :src="file.thumb"
              thumbnail
              fluid
              alt="Thumbnail"
            ></b-img>
            <div v-else class="file-icon">
              <i class="fa fa-file-image"></i>
            </div>
          </div>

          <div class="flex-grow-1">
            <h6 class="mb-1">{{ file.name }}</h6>
            <small class="text-muted">
              {{ (file.size / 1024).toFixed(2) }} KB
            </small>

            <b-progress
              v-if="file.active || file.progress !== '0.00'"
              :value="file.progress * 100"
              height="4px"
              class="mt-2"
            ></b-progress>

            <b-alert
              v-if="file.error"
              show
              variant="danger"
              size="sm"
              class="mt-2 p-2"
            >
              {{ file.error }}
            </b-alert>

            <b-alert
              v-if="file.success"
              show
              variant="success"
              size="sm"
              class="mt-2 p-2"
            >
              Tải lên hoàn tất!
            </b-alert>
          </div>

          <div class="ml-3">
            <b-button
              v-if="file.active"
              variant="outline-secondary"
              size="sm"
              @click="file.cancel()"
            >
              <i class="fa fa-times"></i>
            </b-button>
            <b-button
              v-else-if="file.error && !file.success"
              variant="outline-secondary"
              size="sm"
              @click="file.retry()"
            >
              <i class="fa fa-redo"></i>
            </b-button>
            <b-button
              v-else
              variant="outline-danger"
              size="sm"
              @click="remove(file)"
            >
              <i class="fa fa-trash"></i>
            </b-button>
          </div>
        </b-list-group-item>
      </b-list-group>
    </b-card-body>

    <b-card-footer v-if="files.length > 0" class="d-flex justify-content-end">
      <b-button variant="outline-danger" class="mr-2" @click="clear">
        Xóa tất cả
      </b-button>
      <b-button
        variant="primary"
        :disabled="files.length === 0 || uploading"
        @click="uploadFiles"
      >
        <b-spinner v-if="uploading" small class="mr-1"></b-spinner>
        <i v-else class="fa fa-cloud-upload-alt mr-1"></i>
        {{ uploadButtonText }}
      </b-button>
    </b-card-footer>
  </b-card>
</template>

<script>
import { mapActions } from 'vuex';
import { makeToastFaile } from '@/utils/common';

export default {
  name: 'ImageUploader',
  data() {
    return {
      files: [],
      dropActive: false,
      uploading: false,
      uploadedCount: 0,
      failedCount: 0,
    };
  },
  computed: {
    uploadButtonText() {
      return this.uploading
        ? `Uploading (${this.uploadedCount + this.failedCount}/${
            this.files.length
          })`
        : 'Tải lên tất cả';
    },
  },
  methods: {
    ...mapActions('images', ['uploadImage']),

    openFileSelector() {
      const fileInput = this.$el.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.click();
      } else {
        console.error('File input element not found');
      }
    },

    inputFile(newFile, oldFile) {
      if (newFile && !oldFile) {
        // Add file
        newFile.active = true;
      }

      if (newFile && oldFile) {
        // Update file

        // Get response data
        if (newFile.success && !oldFile.success) {
          // makeToastSuccess(`${newFile.name} uploaded successfully!`);
          this.$bvToast.toast(`${newFile.name} uploaded successfully!`, {
            title: `Thông báo`,
            variant: 'success',
            solid: true,
          });
        }

        // Upload error
        if (newFile.error && !oldFile.error) {
          console.log('newFile.error', newFile.error);
          makeToastFaile(`Failed to upload ${newFile.name}`);
        }
      }

      // Remove file
      if (!newFile && oldFile) {
        if (oldFile.active) {
          oldFile.active = false;
        }
      }
    },

    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        // Validate file type
        if (!/\.(jpeg|jpg|png|gif|webp)$/i.test(newFile.name)) {
          this.$toast.error(
            'Only image files (jpg, png, gif, webp) are allowed!',
          );
          return prevent();
        }

        // Create a thumbnail for preview
        const URL = window.URL || window.webkitURL;
        if (URL && URL.createObjectURL) {
          newFile.thumb = URL.createObjectURL(newFile.file);
        }
      }
    },

    remove(file) {
      this.$refs.fileUpload.remove(file);
    },

    clear() {
      this.$refs.fileUpload.clear();
    },

    async uploadFiles() {
      if (this.files.length === 0) return;

      this.uploading = true;
      this.uploadedCount = 0;
      this.failedCount = 0;

      const uploadPromises = this.files.map(async (file) => {
        try {
          if (file.success) {
            // Skip already uploaded files
            this.uploadedCount++;
            return;
          }

          file.active = true;

          // Manually upload each file
          const response = await this.uploadImage(file.file);

          file.success = response.success;
          file.active = false;

          if (response.success) {
            this.uploadedCount++;
          } else {
            file.error = response.error || 'Upload failed';
            this.failedCount++;
          }
        } catch (error) {
          file.active = false;
          file.error = error.message || 'Upload failed';
          this.failedCount++;
        }
      });

      try {
        await Promise.all(uploadPromises);

        // if (this.uploadedCount > 0) {
        //   this.$bvToast.toast(
        //     `Successfully uploaded ${this.uploadedCount} images`,
        //     {
        //       title: `Thông báo`,
        //       variant: 'success',
        //       solid: true,
        //     },
        //   );
        // }

        if (this.failedCount > 0) {
          makeToastFaile(`Failed to upload ${this.failedCount} images`);
        }

        // Emit event to parent component
        this.$emit('upload-complete', {
          success: this.uploadedCount,
          failed: this.failedCount,
        });
      } catch (error) {
        console.error('Error during batch upload:', error);
        makeToastFaile('An error occurred during upload');
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style scoped>
.image-uploader {
  margin-bottom: 20px;
}

.drop-active {
  margin: 10px 0;
  text-align: center;
  padding: 30px;
  border: 2px dashed #28a745;
  border-radius: 8px;
}

.file-thumbnail {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-thumbnail img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.file-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-size: 1.5rem;
  color: #6c757d;
}
</style>
